import { getCategory } from '@/actions/ecom/category';
import PageLayout from '@/components/Layouts/PageLayout';
import MainNav from '@/components/Product/MainNav';
import Breadcrumb from '@/shared/crisp/Breadcrumb';
import { NextPageContext } from 'next';
import React, { FC, useCallback, useMemo } from 'react';
import useLayoutRenderer from '@/hooks/useLayoutRenderer';
import { MemoExoticComponent } from 'react';
import Container from '@/shared/Container';
import ProductBannerExtraLarge from '@/shared/crisp/ProductBannerExtraLarge';
import HomeProductBanners from '@/components/Home/HomeProductBanners';
import TrendingProducts from '@/components/Home/TrendingProducts';
import WhyBuy from '@/components/Home/WhyBuy';
import getSingleString from '@/utils/string';
import ssrErrorHandler from '@/errors/ssrErrorHandler';
import { getCategoryURL } from '@/utils/urls';
import Div from '@/shared/Div';
import { ECOM_HOME_URL } from '@/configs/constants';
import Accordion from '@/shared/crisp/Accordion';
import SEOContent from '@/components/Seo/Content';
import PLP from '@/components/PLP';
import { getSearchResults } from '@/actions/ecom/search';
import usePLP from '@/hooks/usePLP';
import is from '@/utils/is';
import fetchAndMergeProductsWithPrice from '@/utils/fetchAndMergeProductsWithPrice';
import Heading from '@/shared/Heading';
import Filters from '@/components/PLP/Filters';
import { CMS_COMPONENTS } from '@/constants/cms';
import NewLaunches from '@/components/Home/NewLaunches';
import DynamicPlaceholder from '@/shared/crisp/DynamicPlaceholder';

const STATIC_MAP: { [key: string]: MemoExoticComponent<any> | FC<any> } = {
  [CMS_COMPONENTS.PRODUCT_BANNERS.EXTRA_LARGE]: ProductBannerExtraLarge,
  [CMS_COMPONENTS.PRODUCT_BANNERS.LARGE]: HomeProductBanners,
  [CMS_COMPONENTS.SHARED.TRENDING_PRODUCTS]: TrendingProducts,
  [CMS_COMPONENTS.SHARED.WHY_BUY_FROM_US]: WhyBuy,
  [CMS_COMPONENTS.SHARED.ACCORDION]: Accordion,
  [CMS_COMPONENTS.SHARED.HTML]: SEOContent,
  [CMS_COMPONENTS.SHARED.PLP]: PLP,
  [CMS_COMPONENTS.SHARED.NEW_LAUNCHES]: NewLaunches,
  [CMS_COMPONENTS.SHARED.DYNAMIC_PLACEHOLDER]: DynamicPlaceholder,
};

export default function Category({
  mainNav,
  page,
  seo,
  name,
  plp,
  categoryId,
}: {
  mainNav: any;
  page: any;
  seo: any;
  name: string;
  plp: any;
  categoryId: string;
}) {
  const {
    plpList,
    open,
    isEmptyProductsList,
    closeModal,
    handleOpenModal,
    modalType,
    currentPage,
    applySortByFilter,
    viewMoreProduct,
    isLoading,
    onApplyFilter,
    onApplySortBy,
    onResetFilter,
  } = usePLP(plp, getSearchResults, null, { 'category.id': [categoryId] }, 12);

  let dataSet = useMemo(() => {
    return [
      { id: 1, text: 'Home', link: ECOM_HOME_URL },
      { id: 2, text: name, link: getCategoryURL(categoryId) },
    ];
  }, [categoryId, name]);

  const renderPLPHeader = useCallback(
    (plpProps?: any) => {
      return (
        <Div className="pb-6">
          <Container>
            <Heading level={2} className="py-s6 text-center text-h-2">
              {plpProps?.title}
            </Heading>
            {plpProps?.showFilters ? (
              <Filters
                modalType={modalType}
                closeModal={closeModal}
                open={open}
                filters={plpList.filters}
                onApplyFilter={onApplyFilter}
                onResetFilter={onResetFilter}
                onApplySortBy={onApplySortBy}
                show={!isEmptyProductsList}
                handleOpenModal={handleOpenModal}
                applySortByFilter={applySortByFilter}
              />
            ) : null}
          </Container>
        </Div>
      );
    },
    [
      modalType,
      closeModal,
      open,
      plpList.filters,
      onApplyFilter,
      onResetFilter,
      onApplySortBy,
      isEmptyProductsList,
      handleOpenModal,
      applySortByFilter,
    ],
  );

  const componentProps = useMemo(() => {
    return {
      [CMS_COMPONENTS.SHARED.PLP]: {
        isLoading,
        products: plpList.products,
        resultCount: plpList.resultCount,
        page: currentPage,
        viewMoreProduct,
        renderPLPHeader,
      },
    };
  }, [
    currentPage,
    isLoading,
    plpList.products,
    plpList.resultCount,
    renderPLPHeader,
    viewMoreProduct,
  ]);

  const layout = useLayoutRenderer(page, STATIC_MAP, componentProps);
  return (
    <PageLayout
      mainClassName="font-primary"
      hideFooterIcons
      seo={seo}
      pageWrapperClassName="font-primary "
    >
      <Container>
        <Div className="py-s3">
          <Breadcrumb dataSet={dataSet} className="py-s3" />
        </Div>
        <MainNav subCategories={mainNav?.subCategories} name={mainNav?.name} />
      </Container>
      {layout}
    </PageLayout>
  );
}

export async function getServerSideProps(nextPageContext: NextPageContext) {
  const { query } = nextPageContext;
  const categoryId = getSingleString(query.categoryId);
  try {
    const promises = [
      getCategory(categoryId),
      getSearchResults({
        q: null,
        filters: { 'category.id': [categoryId] },
        page: 1,
        size: 12,
      }),
    ];
    const [page, searchResults] = await Promise.all(promises);
    const { name, subCategories = [], layout, seo } = page;
    const { products, filters, pageCount = 1, resultCount = 0 } = searchResults?.data;

    const isPLPInLayout = Boolean(
      layout.find((item: any) => item.__component === CMS_COMPONENTS.SHARED.PLP),
    );

    let productsWithPrice: any = [];
    if (isPLPInLayout && is.not.empty.array(products)) {
      productsWithPrice = (await fetchAndMergeProductsWithPrice(products || [])) ?? null;
    }

    return {
      props: {
        mainNav: {
          name,
          subCategories,
        },
        page: layout,
        seo,
        plp: { products: productsWithPrice, filters, pageCount, resultCount },
        name,
        categoryId,
      },
    };
  } catch (error) {
    return ssrErrorHandler(error, nextPageContext);
  }
}
